// Lib
import React, { useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { useDispatch } from 'react-redux';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import ElementFeatureSuggestion from './ElementFeatureSuggestion';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ELEMENT_FEATURES } from '../feature/elementFeatureConstants';
import { convertElementsToDocument } from '../document/documentActions';
import { useActiveTiptapEditorCallback } from '../../components/tiptapEditor/store/tiptapEditorStoreHooks';
import { DispatchUpdateElement } from '../actions/elementActionTypes';

interface ConvertToDocumentFeatureSuggestionProps {
    element: ImMNElement;
    added: boolean;
    question: string | React.ReactElement;
    dispatchUpdateElement: DispatchUpdateElement;
    dispatchToggleElementFeature: (feature: string, elements: ImMNElement[]) => void;
}

const ConvertToDocumentFeatureSuggestion: React.FC<ConvertToDocumentFeatureSuggestionProps> = (props) => {
    const { element, added, question, dispatchUpdateElement, dispatchToggleElementFeature } = props;

    const elementId = getElementId(element);

    const activeTiptapEditorRef = useRef<Editor>();

    const dispatch = useDispatch();
    const dispatchConvertElementsToDocument = (elementIds: string[]) => dispatch(convertElementsToDocument(elementIds));

    /**
     * This works in conjunction with the ElementContainer component to prevent
     * clicks from being handled in the ElementContainer when they're already handled elsewhere.
     */
    const onMouseDown = useCallback((event: React.MouseEvent) => {
        // @ts-ignore - Custom property added to the event and used within ElementContainer
        event.handled = true;
    }, []);

    /**
     * Prior to focus being lost by the Tiptap editor, capture the active editor instance,
     * if one exists, so we can refocus it later.
     */
    const onPointerDown = useActiveTiptapEditorCallback((activeEditor) => {
        if (!activeEditor) return;
        activeTiptapEditorRef.current = activeEditor;
    }, []);

    /**
     * If there was an active Tiptap editor when the suggestion was dismissed,
     * refocus it to allow the user to continue editing.
     */
    const onDismiss = useCallback(() => {
        if (!activeTiptapEditorRef.current) return;
        activeTiptapEditorRef.current.commands.focus();
    }, []);

    const onConfirm = useCallback(() => {
        dispatchConvertElementsToDocument([elementId]);
    }, [elementId]);

    return (
        <ElementFeatureSuggestion
            element={element}
            question={question}
            feature={ELEMENT_FEATURES.DOCUMENT}
            dispatchUpdateElement={dispatchUpdateElement}
            dispatchToggleElementFeature={dispatchToggleElementFeature}
            onDismiss={onDismiss}
            onConfirm={onConfirm}
            onPointerDown={onPointerDown}
            onMouseDown={onMouseDown}
            added={added}
        />
    );
};

export default ConvertToDocumentFeatureSuggestion;
